import React from 'react';
import { useTranslation } from 'react-i18next';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  Grid,
  TextField,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { activityTypeKeys, industryAndServiceKeys, CustomField } from '../../types/user';

interface Props {
  firstName: string;
  lastName: string;
  username: string;
  activityType: CustomField;
  specialization: CustomField; // This is actually ambassadorSpecialization
  ambassadorDescription: string;
  followersCount: number;
  onInputChange: (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSelectChange: (field: string) => (event: SelectChangeEvent, isCustomField?: boolean) => void;
}

const AmbassadorFields: React.FC<Props> = ({
  firstName,
  lastName,
  username,
  activityType,
  specialization, // This is actually ambassadorSpecialization
  ambassadorDescription,
  followersCount,
  onInputChange,
  onSelectChange,
}) => {
  const { t } = useTranslation();

  return (
    <Paper elevation={0} className="glass-effect" sx={{ p: 3, borderRadius: 2 }}>
      <Typography 
        variant="h6" 
        className="animated-gradient-text" 
        gutterBottom 
        sx={{ 
          mb: 4,
          fontFamily: 'Poppins, sans-serif'
        }}
      >
        {t('registration.generalInfo')}
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('registration.lastName')}
            value={lastName}
            onChange={onInputChange('lastName')}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('registration.firstName')}
            value={firstName}
            onChange={onInputChange('firstName')}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('registration.username')}
            value={username}
            onChange={onInputChange('username')}
            required
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth required sx={{ '& .MuiInputBase-root': { color: 'inherit' } }}>
            <InputLabel>{t('registration.activityType')}</InputLabel>
            <Select
              value={activityType.value}
              onChange={(e) => onSelectChange('activityType')(e, true)}
              label={t('registration.activityType')}
            >
              {activityTypeKeys.map(key => (
                <MenuItem key={key} value={key}>
                  {t(`registration.activityTypes.${key}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {activityType.isOther && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label={t('registration.activityTypes.other_placeholder')}
              value={activityType.customValue || ''}
              onChange={onInputChange('activityType.customValue')}
              required
              variant="outlined"
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <FormControl fullWidth sx={{ '& .MuiInputBase-root': { color: 'inherit' } }}>
            <InputLabel>{t('registration.specialization')}</InputLabel>
            <Select
              value={specialization.value}
              onChange={(e) => onSelectChange('ambassadorSpecialization')(e, true)}
              label={t('registration.specialization')}
            >
              {industryAndServiceKeys.map(key => (
                <MenuItem key={key} value={key}>
                  {t(`registration.industries.${key}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {specialization.isOther && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label={t('registration.industries.other_placeholder')}
              value={specialization.customValue || ''}
              onChange={onInputChange('ambassadorSpecialization.customValue')}
              required
              variant="outlined"
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t('registration.ambassador.description')}
            placeholder={t('registration.ambassador.description_placeholder')}
            value={ambassadorDescription}
            onChange={onInputChange('ambassadorDescription')}
            required
            variant="outlined"
          />
        </Grid>
        {activityType.value === 'influencer' && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('registration.followersCount')}
              value={followersCount}
              onChange={onInputChange('followersCount')}
              type="number"
              variant="outlined"
              required
            />
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default AmbassadorFields;
